// Ad Preview Mockups Stage 1 §13.11. Pair each rendered asset mockup
// with one of the Campaign's text variations. The pairing is
// DETERMINISTIC per asset — same asset id always lands on the same
// headline + body, so a franchisee opening the preview a second time
// sees identical mockups (no "the ad changed!" confusion on refresh).
//
// Why deterministic instead of random:
// - Refresh / share-link-twice always shows the same thing → no
//   confusion when the franchisee discusses the preview with the AM
//   ("the third mockup" means the same thing across sessions).
// - Stable across deploys — pairing only changes if the Campaign's
//   text arrays or the asset list change.
//
// Algorithm: FNV-1a 32-bit hash of asset.driveFileId modulo each
// array's length picks the entry. Cheap, dependency-free, well-
// distributed across the limited number of asset ids per Campaign.
//
// Pure helper: no React, no I/O. Consumed by PreviewPage which feeds
// each Story / Feed mockup its own headline + body.

import type { CtaOption } from '../types/campaign.js';
import type { HydratedAsset } from '../modules/preview/publicDto.js';

export type AssetTextPairing = {
  asset: HydratedAsset;
  headline: string | null;
  body: string | null;
  cta: CtaOption | null;
};

// FNV-1a 32-bit. Returns an unsigned integer.
//
// Deliberately not crypto-strong — the hash only needs to spread asset
// ids across a small array index space. Collisions don't matter for
// this use case (two assets happening to pick the same index just
// means they share text, which already happens when the arrays are
// shorter than the asset count).
function fnv1aHash(str: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    // Multiply by FNV prime 16777619 mod 2^32. Using Math.imul for
    // proper 32-bit multiply semantics in JS.
    hash = Math.imul(hash, 0x01000193);
  }
  // Coerce to unsigned 32-bit.
  return hash >>> 0;
}

function pickByHash<T>(items: readonly T[], hash: number): T | null {
  if (items.length === 0) return null;
  return items[hash % items.length] ?? null;
}

// Optional: a small offset so headline and body don't always pick the
// same index for a given asset. Without this, asset id "X" with hash
// 5 would pick headlines[5 % H] AND body[5 % B] — fine but slightly
// less varied across a list of assets. We add a constant offset for
// body so the two arrays sample independently.
const BODY_HASH_OFFSET = 0xdeadbeef;

export function pairAssetsWithText(opts: {
  assets: readonly HydratedAsset[];
  headlines: readonly string[];
  bodyCopies: readonly string[];
  ctaButton: CtaOption | null;
}): AssetTextPairing[] {
  const cleanHeadlines = opts.headlines
    .map((h) => h.trim())
    .filter((h) => h.length > 0);
  const cleanBodies = opts.bodyCopies
    .map((b) => b.trim())
    .filter((b) => b.length > 0);

  return opts.assets.map((asset) => {
    const headlineHash = fnv1aHash(asset.driveFileId);
    const bodyHash = fnv1aHash(asset.driveFileId) ^ BODY_HASH_OFFSET;
    return {
      asset,
      headline: pickByHash(cleanHeadlines, headlineHash),
      body: pickByHash(cleanBodies, bodyHash >>> 0),
      cta: opts.ctaButton,
    };
  });
}
